import axios from 'axios';
import { useEffect, useState } from 'react';
import { getAuthUser } from '../Authentication';

interface APIResponse {
  status?: boolean;
  msg?: string;
  statusCode?: number;
  errorMsg?: string;
  [key: string]: any;
}

const API = axios.create({
  transformResponse: (data) => {
    const response = JSON.parse(data);
    return response;
  },
});

API.defaults.headers.post['Content-Type'] = 'application/json';

export function axiosInit() {
  API.defaults.baseURL = process.env.REACT_APP_SAAS_URL;
}

/**
 * 获取Foodifox的数据，异步操作，then获取数据或await同步获取数据
 * @param {string} url
 * @param param
 * @returns {Promise<any | null>}
 */
export async function request(url: string, param?: any): Promise<any | null> {
  const authUser = getAuthUser();
  if (authUser) {
    const token = await authUser.getIdToken();
    API.defaults.headers.post.Authorization = `Bearer: ${token}`;
  }
  const { data } = await API.post<APIResponse>(url, param);
  const { status, paramBean, msg, errorMsg } = data;
  if (msg || errorMsg) {
    console.log('Foodifox的rest请求时，Url: ', url, '，Msg: ', msg || errorMsg);
  }

  if (!status) {
    throw new Error(errorMsg || msg || 'Server error');
  }
  return paramBean != null ? paramBean : data;
}

/**
 * Lazy方式查询rest接口
 * 使用示例：const [queryMethod, { data, loading }] = getFoodifoxDataLazy('url');
 * queryMethod：是自定义命名的方法名，使用时：queryMethod({ param: {参数} });
 */
type DataLazyData<T> = [
  (option?: {
    param?: any;  // 参数
    onCompleted?: (data: T) => void;  // 执行接口方法完成时触发
    onError?: (error: Error) => void; // 执行接口方法报错时触发
  }) => void,
  {
    data: T | null;
    loading: boolean;
  }
];
export function getFoodifoxDataLazy<T>(url: string, optionParam?: { onCompleted?: (data: T) => void; onError?: (error: Error) => void }): DataLazyData<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const execUpdate = async (option?: {
    param?: any;
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }) => {
    setLoading(true);
    request(url, option?.param).then((resultData) => {
      setData(resultData);
      if (option?.onCompleted) {
        option?.onCompleted(resultData);
      }
    }).catch((e) => {
      setData(null);
      if (option?.onError) {
        option?.onError(e || 'Server error');
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  return [
    (option?: {
      param?: any;
      onCompleted?: (data: T) => void;
      onError?: (error: Error) => void;
    }) => execUpdate({ ...option, ...optionParam }),
    {
      data,
      loading,
    },
  ];
}

/**
 * 获取Foodifox的数据
 * @param {string} url
 * @param param
 * @returns {Promise<any | null>}
 * 使用示例：const { data, loading } = getFoodifoxData('url');
 */
type DataResult<T> = {
  data: T | null;
  loading: boolean;
  refetch: () => void;
};
export function getFoodifoxData<T>(url: string, param?: any): DataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refetchTime, setRefetchTime] = useState<number>(0);

  useEffect(() => {
    setLoading(true);
    request(url, param).then((resultData) => {
      setData(resultData);
    }).catch(() => {
      setData(null);
    }).finally(() => {
      setLoading(false);
    });
  }, [refetchTime]);

  return {
    data,
    loading,
    refetch: () => setRefetchTime(new Date().getTime()),
  };
}

/**
 * 更新Foodifox的数据
 * @param url
 * 使用示例：const [updateMethod, { data, loading }] = updateFoodifoxData('url');
 * updateMethod：是自定义命名的方法名，使用时：updateMethod({ param: {参数} });
 */
type DataUpdate<T> = [
  (option?: {
    param?: any;  // 参数
    onCompleted?: (data: T) => void;  // 执行接口方法完成时触发
    onError?: (error: Error) => void; // 执行接口方法报错时触发
  }) => void,
  {
    data: T | null;
    loading: boolean;
  }
];
export function updateFoodifoxData<T>(url: string, optionParam?: { onCompleted?: (data: T) => void; onError?: (error: Error) => void }): DataUpdate<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const execUpdate = async (option?: {
    param?: any;
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }) => {
    setLoading(true);
    request(url, option?.param).then((resultData) => {
      setData(resultData);
      if (option?.onCompleted) {
        option?.onCompleted(resultData);
      }
    }).catch((e) => {
      setData(null);
      if (option?.onError) {
        option?.onError(e || 'Server error');
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  return [
    (option?: {
      param?: any;
      onCompleted?: (data: T) => void;
      onError?: (error: Error) => void;
    }) => execUpdate({ ...option, ...optionParam }),
    {
      data,
      loading,
    },
  ];
}

/**
 * 增加Foodifox的Log数据
 * @param {string} url
 * @param param
 * @returns {Promise<any | null>}
 */
export async function addFoodifoxLog(url: string, param?: any): Promise<any | null> {
  const { data } = await API.post<APIResponse>(url, param);
  const { status, paramBean, msg, errorMsg } = data;
  if (msg || errorMsg) {
    console.log('增加Foodifox的Log数据时，Url: ', url, '，Msg: ', msg || errorMsg);
  }

  if (!status) {
    throw new Error(errorMsg || 'Server error');
  }
  return paramBean || null;
}

export default {
  axiosInit,
  request,
  getFoodifoxDataLazy,
  getFoodifoxData,
  updateFoodifoxData,
  addFoodifoxLog,
};
